// User & Auth
export interface User {
  id: number;
  phone: string | null;
  display_name: string | null;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  origin_country: string | null;
  origin_university: string | null;
  field_of_study: string | null;
  graduation_year: number | null;
  goal: UserGoal | null;
  onboarding_step: OnboardingStep;
  onboarding_completed: boolean;
  ghadam_balance: number;
  matching_profile: MatchingProfile | null;
  matching_profile_completed: boolean;
  created_at: string;
  auth_provider?: 'phone' | 'google' | 'basic';
  picture_url?: string | null;
  email_verified?: boolean | null;
  is_admin?: boolean;
}

export type UserGoal = 'applying' | 'applied' | 'browsing';
export type OnboardingStep = 'signed_up' | 'goal_selected' | 'first_program_added' | 'profile_started' | 'profile_completed' | 'completed';

// Matching Profile
export interface MatchingProfile {
  preferred_fields: string[];
  preferred_countries: string[];
  budget_min?: number;
  budget_max?: number;
  budget_currency?: Currency;
  budget_max_eur?: number;
  preferred_degree_level?: string;
  target_intake?: string;
  language_preference?: string;
  ielts_score?: number;
  toefl_score?: number;
  gre_score?: number;
  gmat_score?: number;
  gpa?: number;
  gpa_scale?: string;
  prefer_scholarships?: boolean;
}

export interface MatchingOptions {
  fields: string[];
  field_options?: Array<{ field: string; count: number }>;
  countries: string[];
  country_options?: Array<{ country: string; count: number }>;
  budget_ranges: Record<string, { min: number; max: number; label: string }>;
  intake_options: { value: string; label: string }[];
  degree_levels: string[];
  teaching_languages: string[];
  gpa_scales?: string[];
  budget_currencies?: Currency[];
}

export interface Recommendation {
  id: number;
  program_name: string;
  university_name: string | null;
  country: string | null;
  city: string | null;
  degree_level: string | null;
  field_of_study: string | null;
  tuition_fee: number | null;
  teaching_language: string | null;
  duration_months: number | null;
  application_deadline: string | null;
  university_ranking_qs: number | null;
  scholarship_available: boolean;
  match_score: number;
  match_reasons: string[];
  warnings: string[];
  match_explanations?: MatchExplanation[];
}

export interface MatchExplanation {
  code: string;
  kind: 'strength' | 'warning' | 'info';
  label: string;
  detail: string | null;
  points: number;
}

export interface RecommendationRefinement {
  code: string;
  label: string;
  detail: string;
}

export interface RecommendationThreshold {
  max_results: number;
  uncapped_total?: number;
  capped: boolean;
  too_many?: boolean;
}

export interface RecommendationsResponse {
  recommendations: Recommendation[];
  total: number;
  limit: number;
  offset: number;
  profile_summary: {
    fields: string[];
    countries: string[];
    degree_level: string | null;
    budget_max: number | null;
    budget_currency?: Currency | null;
    budget_max_eur?: number | null;
    language_preference?: string | null;
    gpa?: number | null;
    gpa_scale?: string | null;
  };
  refinement_prompts?: RecommendationRefinement[];
  result_threshold?: RecommendationThreshold;
}

export interface AuthResponse {
  token: string;
  user: User;
  is_new_user: boolean;
}

export interface SignupRequest {
  first_name: string;
  last_name: string;
  phone: string;
  password: string;
  email?: string;
}

/** Either the access_token from the emailed reset link, or the email + 6-digit code. */
export interface PasswordResetConfirm {
  password: string;
  access_token?: string;
  email?: string;
  code?: string;
}

export interface PasswordLoginRequest {
  phone: string;
  password: string;
}

// University & Course
export interface University {
  id: number;
  name: string;
  name_local: string | null;
  country: string;
  city: string;
  website: string | null;
  logo_url: string | null;
  ranking_qs: number | null;
  ranking_the: number | null;
  ranking_shanghai: number | null;
  university_type: string | null;
  course_count?: number;
}

export interface Course {
  id: number;
  university_id: number;
  name: string;
  degree_level: DegreeLevel;
  field: string;
  teaching_language: TeachingLanguage;
  duration_months: number | null;
  tuition_fee_amount: number | null;
  tuition_fee_currency: Currency | null;
  is_tuition_free: boolean;
  application_fee_amount: number | null;
  application_fee_currency: Currency | null;
  fee_waiver_available: boolean;
  fee_waiver_conditions?: string | null;
  deadline_fall: string | null;
  deadline_spring: string | null;
  gpa_minimum: number | null;
  gre_required: boolean;
  scholarships_available: boolean;
  scholarship_details?: string | null;
  program_url: string | null;
  application_url?: string | null;
  description?: string | null;
  notes?: string | null;
  deadline_notes?: string | null;
  last_verified_at?: string | null;
  last_crawled_at?: string | null;
  source_name?: string | null;
  source_url?: string | null;
  tuition_confidence?: string | null;
  deadline_confidence?: string | null;
  requirements_confidence?: string | null;
  fee_confidence?: string | null;
  created_at?: string;
  updated_at?: string;
  view_count?: number;
  university_name?: string;
  university_country?: string;
  university_city?: string;
  university_ranking_qs?: number | null;
}

export interface CourseLanguageRequirement {
  id: number;
  course_id: number;
  test_type: string;
  minimum_overall: number | null;
  minimum_reading: number | null;
  minimum_writing: number | null;
  minimum_speaking: number | null;
  minimum_listening: number | null;
  cefr_level: string | null;
  certificate_level: string | null;
  notes: string | null;
  is_mandatory: boolean;
}
export interface CourseSummary {
  id: number;
  name: string;
  degree_level: DegreeLevel;
  university_name: string;
  university_country: string;
  deadline_fall: string | null;
}

// Paginated courses response
export interface CoursesResponse {
  courses: Course[];
  total: number;
  limit: number;
  offset: number;
}

// Course search parameters
export interface CourseSearchParams {
  query?: string;
  field?: string;
  fields?: string[];
  country?: string;
  countries?: string[];
  degree_level?: DegreeLevel | '';
  teaching_language?: TeachingLanguage | '';
  max_tuition?: number;
  tuition_free_only?: boolean;
  low_barrier_only?: boolean;
  deadline_after?: string;
  deadline_before?: string;
  limit?: number;
  offset?: number;
}

export type DegreeLevel = 'bachelor' | 'master' | 'phd' | 'diploma' | 'certificate';
export type TeachingLanguage = 'english' | 'german' | 'french' | 'dutch' | 'spanish' | 'italian' | 'swedish' | 'norwegian' | 'danish' | 'finnish' | 'polish' | 'czech' | 'japanese' | 'chinese' | 'korean' | 'other';
export type Currency = 'EUR' | 'USD' | 'CAD' | 'AUD' | 'GBP' | 'CHF' | 'SEK' | 'NOK' | 'DKK' | 'JPY' | 'CNY' | 'IRR';

export interface CurrencyRate {
  currency_code: string;
  rate_to_irr: number;
  rate_to_usd: number;
  last_updated: string;
}

// Source metrics
export interface SourceInfo {
  source: string;
  total_items: number;
  parse_status: {
    pending: number;
    parsed: number;
    needs_llm: number;
    done: number;
    failed: number;
  };
  last_crawl: string | null;
  last_parse: string | null;
  courses_in_catalogue: number;
  universities_in_catalogue: number;
}

export interface SourcesResponse {
  sources: SourceInfo[];
  summary: {
    total_courses: number;
    courses_with_known_source: number;
    courses_without_source: number;
  };
}

// Tracked Program
export interface TrackedProgram {
  id: number;
  user_id: number;
  course_id: number | null;
  custom_program_name: string | null;
  custom_university_name: string | null;
  custom_country: string | null;
  custom_deadline: string | null;
  status: ApplicationStatus;
  priority: Priority;
  intake: IntakePeriod | null;
  deadline: string | null;
  submitted_date: string | null;
  result_date: string | null;
  interview_date?: string | null;
  reminders_enabled?: boolean;
  reminder_offsets_days?: number[] | null;
  next_reminder_at?: string | null;
  // Sharing state (experience platform)
  shared_as_experience?: boolean;
  shared_experience_id?: number | null;
  shared_at?: string | null;
  notes: string | null;
  notes_entries: NoteEntry[] | null;
  document_checklist: ChecklistItem[] | null;
  application_portal_url: string | null;
  application_id: string | null;
  scholarship_offered: boolean;
  scholarship_amount: number | null;
  match_score: number | null;
  match_reasons?: string[] | null;
  match_warnings?: string[] | null;
  matching_profile_snapshot?: MatchingProfile | null;
  recommendation_snapshot?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  // Joined data
  program_name?: string;
  university_name?: string;
  country?: string;
  city?: string;
  university_ranking_qs?: number | null;
  degree_level?: string;
  program_deadline?: string | null;
  
}

export interface ChecklistItem {
  id: string;
  name: string;
  required: boolean;
  completed: boolean;
  notes?: string;
  due_date?: string;
}

export type NoteCategory = 'important' | 'contact' | 'link' | 'reminder' | 'requirement' | 'deadline' | 'funding' | 'portal' | 'general';

export interface NoteEntry {
  id: string;
  content: string;
  category: NoteCategory;
  pinned: boolean;
  created_at: string;
  updated_at: string;
}

export const NOTE_CATEGORIES: Record<NoteCategory, { labelKey: string; icon: string; color: string }> = {
  important: { labelKey: 'notes.categories.important', icon: '⚡', color: 'bg-status-danger/10 text-status-danger border border-status-danger/30' },
  contact: { labelKey: 'notes.categories.contact', icon: '👤', color: 'bg-status-info/10 text-status-info border border-status-info/30' },
  link: { labelKey: 'notes.categories.link', icon: '🔗', color: 'bg-brand-secondary/10 text-brand-secondary border border-brand-secondary/30' },
  reminder: { labelKey: 'notes.categories.reminder', icon: '⏰', color: 'bg-status-warning/10 text-status-warning border border-status-warning/30' },
  requirement: { labelKey: 'notes.categories.requirement', icon: '📋', color: 'bg-brand-primary/10 text-brand-primary border border-brand-primary/30' },
  deadline: { labelKey: 'notes.categories.deadline', icon: '📅', color: 'bg-status-danger/10 text-status-danger border border-status-danger/30' },
  funding: { labelKey: 'notes.categories.funding', icon: '💰', color: 'bg-brand-accent/10 text-brand-accent border border-brand-accent/30' },
  portal: { labelKey: 'notes.categories.portal', icon: '🔐', color: 'bg-status-info/10 text-status-info border border-status-info/30' },
  general: { labelKey: 'notes.categories.general', icon: '📝', color: 'bg-elevated text-text-secondary border border-border' },
};

export type ApplicationStatus = 
  | 'researching' 
  | 'preparing' 
  | 'submitted' 
  | 'under_review' 
  | 'interview' 
  | 'waitlisted' 
  | 'accepted' 
  | 'rejected' 
  | 'withdrawn'
  | 'deferred';

export type Priority = 'dream' | 'target' | 'safety';
export type IntakePeriod = 'fall_2025' | 'spring_2026' | 'fall_2026' | 'spring_2027' | 'fall_2027';

export interface TrackerStats {
  total_programs: number;
  by_status: Record<string, number>;
  by_priority: Record<string, number>;
  accepted_count: number;
  rejected_count: number;
  pending_count: number;
  upcoming_deadlines: number;
}

export interface DeadlineItem {
  id: number;
  program_name: string;
  university_name: string;
  deadline: string;
  days_until: number;
  status: ApplicationStatus;
  urgency?: 'overdue' | 'today' | 'urgent' | 'soon' | 'future';
}

export interface ReminderItem {
  program_id: number;
  program_name: string;
  university_name: string;
  reminder_date: string;
  deadline: string;
  offset_days: number;
  days_until_deadline: number;
  status: ApplicationStatus;
}

export interface PrintablePlan {
  generated_at: string;
  programs: Array<{
    id: number;
    program_name: string;
    university_name: string;
    country: string | null;
    status: ApplicationStatus;
    priority: Priority;
    intake: IntakePeriod | null;
    deadline: string | null;
    submitted_date: string | null;
    result_date: string | null;
    interview_date: string | null;
    application_portal_url: string | null;
    application_id: string | null;
    checklist: {
      total: number;
      completed: number;
      required_total: number;
      required_completed: number;
      pending_documents: ChecklistItem[];
      pending_required_documents: ChecklistItem[];
    };
    notes_count: number;
    reminders_enabled: boolean;
    reminder_offsets_days: number[];
    next_reminder_at: string | null;
    match_score: number | null;
  }>;
  summary: {
    total_programs: number;
    upcoming_deadlines_30_days: number;
    pending_required_documents: number;
  };
}

export interface ApplicationCosts {
  application_fees: {
    items: Array<{
      program_id: number;
      program_name: string;
      university_name: string;
      amount: number;
      currency: string;
      fee_waiver_available: boolean;
    }>;
    programs_with_known_fee: number;
    programs_fee_unknown: number;
  };
  exam_costs: {
    total_irr: number;
    breakdown: Array<{
      test_type: string;
      amount_local: number;
      currency_local: string;
      amount_irr: number | null;
      city: string | null;
      source: string;
    }>;
  };
  total_programs_tracked: number;
}


export type ExperienceVisibility = 'private' | 'anonymized' | 'public';
export type ExperienceModerationStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'hidden';
export type ExperienceApplicationStatus = 'preparing' | 'submitted' | 'under_review' | 'interview' | 'accepted' | 'rejected' | 'waitlisted' | 'withdrawn';
export type ExperienceDegreeLevel = 'masters' | 'phd' | 'mba' | 'postdoc';

export interface PublicExperience {
  id: number;
  university_id: number | null;
  course_id: number | null;
  university_name: string | null;
  country: string | null;
  city: string | null;
  program_name: string;
  department: string | null;
  degree_level: ExperienceDegreeLevel;
  application_year: number;
  application_round: string | null;
  status: ExperienceApplicationStatus;
  decision_date: string | null;
  scholarship_applied: boolean;
  scholarship_received: boolean;
  scholarship_name: string | null;
  scholarship_amount: string | null;
  notes: string | null;
  interview_experience: string | null;
  advice_for_applicants: string | null;
  timeline_notes: string | null;
  would_recommend: boolean | null;
  visibility: ExperienceVisibility;
  view_price: number;
  requires_unlock: boolean;
  applicant_display_name: string | null;
  applicant_degree_level: string | null;
  applicant_major: string | null;
  created_at: string;
}

export interface ExperienceRecord extends PublicExperience {
  applicant_id: number;
  application_deadline: string | null;
  submitted_date: string | null;
  how_found: string | null;
  moderation_status: ExperienceModerationStatus;
  pii_warning_accepted: boolean;
  source_tracked_program_id: number | null;
  submitted_for_review_at: string | null;
  reviewed_at: string | null;
  reviewer_id: number | null;
  moderation_notes: string | null;
}

export interface ExperienceSubmission {
  visibility: ExperienceVisibility;
  status?: ExperienceApplicationStatus;
  program_name?: string;
  university_name?: string;
  country?: string;
  city?: string;
  university_id?: number;
  course_id?: number;
  degree_level?: ExperienceDegreeLevel;
  application_year: number;
  application_round?: string;
  application_deadline?: string | null;
  submitted_date?: string | null;
  decision_date?: string | null;
  scholarship_applied?: boolean;
  scholarship_received?: boolean;
  scholarship_name?: string;
  scholarship_amount?: string;
  notes: string;
  interview_experience?: string;
  advice_for_applicants?: string;
  timeline_notes?: string;
  how_found?: string;
  would_recommend?: boolean | null;
  pii_warning_accepted: boolean;
}

// Coins
export interface GhadamTransaction {
  id: number;
  transaction_type: string;
  amount: number;
  balance_after: number;
  description: string | null;
  created_at: string;
}

// API
export interface CreateTrackedProgramRequest {
  course_id?: number;
  custom_program_name?: string;
  custom_university_name?: string;
  custom_country?: string;
  custom_deadline?: string;
  priority?: Priority;
  intake?: IntakePeriod;
  notes?: string;
}

export interface UpdateTrackedProgramRequest {
  status?: ApplicationStatus;
  priority?: Priority;
  intake?: IntakePeriod;
  deadline?: string | null;
  submitted_date?: string | null;
  result_date?: string | null;
  interview_date?: string | null;
  reminders_enabled?: boolean;
  reminder_offsets_days?: number[] | null;
  next_reminder_at?: string | null;
  notes?: string;
  document_checklist?: ChecklistItem[];
  application_portal_url?: string;
  application_id?: string;
  scholarship_offered?: boolean;
  scholarship_amount?: number;
  shared_as_experience?: boolean;
  shared_experience_id?: number | null;
  shared_at?: string | null;
}

// Display helpers
export const STATUS_LABELS: Record<ApplicationStatus, string> = {
  researching: 'status.researching',
  preparing: 'status.preparing',
  submitted: 'status.submitted',
  under_review: 'status.underReview',
  interview: 'status.interview',
  waitlisted: 'status.waitlisted',
  accepted: 'status.accepted',
  rejected: 'status.rejected',
  withdrawn: 'status.withdrawn',
  deferred: 'status.deferred',
};

export const STATUS_COLORS: Record<ApplicationStatus, string> = {
  researching: 'bg-elevated text-text-secondary border border-border',
  preparing: 'bg-status-info/10 text-status-info border border-status-info/30',
  submitted: 'bg-brand-secondary/10 text-brand-secondary border border-brand-secondary/30',
  under_review: 'bg-status-warning/10 text-status-warning border border-status-warning/30',
  interview: 'bg-status-warning/10 text-status-warning border border-status-warning/30',
  waitlisted: 'bg-status-warning/10 text-status-warning border border-status-warning/30',
  accepted: 'bg-status-success/10 text-status-success border border-status-success/30',
  rejected: 'bg-status-danger/10 text-status-danger border border-status-danger/30',
  withdrawn: 'bg-elevated text-text-muted border border-border',
  deferred: 'bg-brand-primary/10 text-brand-primary border border-brand-primary/30',
};

export const PRIORITY_LABELS: Record<Priority, string> = {
  dream: 'priority.dream',
  target: 'priority.target',
  safety: 'priority.safety',
};

export const PRIORITY_COLORS: Record<Priority, string> = {
  dream: 'bg-gradient-to-r from-brand-secondary to-brand-primary text-white',
  target: 'bg-gradient-to-r from-brand-primary to-status-info text-white',
  safety: 'bg-gradient-to-r from-status-success to-brand-accent text-white',
};

// Match score color helper
export const getMatchScoreColor = (score: number): string => {
  if (score >= 80) return 'text-status-success bg-status-success/10';
  if (score >= 60) return 'text-status-info bg-status-info/10';
  if (score >= 40) return 'text-status-warning bg-status-warning/10';
  return 'text-text-secondary bg-elevated';
};
// Community reports (crowdsourced, time-sensitive signals)
export type ReportType = 'test_seat_availability' | 'embassy_status' | 'payment_method';
export type Staleness = 'fresh' | 'aging' | 'stale' | 'archived';
export type ReportConfidence = 'unconfirmed' | 'corroborated' | 'verified';
export type ReportModeration = 'pending' | 'approved' | 'rejected' | 'hidden';

export type LanguageTestType =
  | 'ielts' | 'toefl_ibt' | 'pte' | 'duolingo' | 'gre' | 'goethe' | 'testdaf' | 'tcf' | 'tef' | 'delf' | 'other';
export type SeatStatus = 'available' | 'limited' | 'full';

export interface TestSeatContent {
  test_type: LanguageTestType;
  city: string;
  seats: SeatStatus;
  test_date?: string;
  test_center?: string;
  price_irr?: number;
  note?: string;
}

export interface CommunityReport {
  id: number;
  report_type: ReportType;
  subject_key: string;
  content: Record<string, unknown>;
  observed_at: string;
  staleness: Staleness;
  age_hours: number;
  confidence: ReportConfidence;
  corroboration_count: number;
  reviewed: boolean;
}

export interface CommunityReportAdmin extends CommunityReport {
  reporter_id: number | null;
  source_type: string;
  source_channel: string | null;
  source_message_id: string | null;
  created_at: string;
  moderation_status: ReportModeration;
  moderation_notes: string | null;
}

export interface TestSession {
  id: number;
  test_type: LanguageTestType;
  city: string;
  test_center: string | null;
  session_date: string;
  registration_deadline: string | null;
  format: 'paper' | 'computer' | 'home' | null;
  price_amount: number | null;
  price_currency: string | null;
  price_irr: number | null;
  source: 'official' | 'authorized_center' | 'admin';
  source_url: string | null;
  last_checked_at: string;
}

// Personal path
export type AcademicStage =
  | 'high_school' | 'bsc_student' | 'bsc_graduate' | 'msc_student' | 'msc_graduate'
  | 'phd_applicant' | 'language_course' | 'returning_after_rejection' | 'exploring';
export type PathTargetDegree = 'bachelor' | 'master' | 'phd' | 'language_course';
export type PathLanguageStatus = 'not_started' | 'booked' | 'taken_waiting' | 'score_ready' | 'expired';
export type PathDocumentKey = 'transcript' | 'degree_certificate' | 'cv' | 'sop' | 'recommendation_letters';

export interface PathProfile {
  academic_stage: AcademicStage;
  current_degree?: string | null;
  target_degree?: PathTargetDegree | null;
  field_of_study?: string | null;
  graduation_year?: number | null;
  gpa?: number | null;
  gpa_scale?: '4' | '20' | '100' | null;
  target_intake_year?: number | null;
  target_intake_term?: 'fall' | 'spring' | null;
  target_countries?: string[];
  language_test_status?: PathLanguageStatus;
  language_test_type?: LanguageTestType | null;
  language_score?: number | null;
  language_test_date?: string | null;
  city_for_tests?: string | null;
  budget_amount?: number | null;
  budget_currency?: 'EUR' | 'USD' | 'CAD' | 'GBP' | 'SEK' | 'DKK' | 'IRT';
  funding_source?: 'family' | 'savings' | 'scholarship_required' | 'part_time_needed' | 'unknown';
  can_pay_application_fees?: boolean | null;
  documents?: Partial<Record<PathDocumentKey, boolean>>;
  translation_attestation_status?: 'not_started' | 'in_progress' | 'done';
  passport_status?: 'valid' | 'expiring_soon' | 'applied' | 'none' | null;
  military_service_status?: 'not_applicable' | 'exempt' | 'completed' | 'student_deferral' | 'unresolved' | null;
  payment_access?: 'has_foreign_card' | 'family_abroad' | 'needs_intermediary' | 'unknown';
  can_travel_for_embassy?: boolean | null;
  risk_tolerance?: 'low' | 'medium' | 'high';
}

export interface PathProfileResponse {
  profile: PathProfile | null;
  source: string | null;
  confirmed: boolean;
  suggested: Partial<PathProfile> | null;
  updated_at: string | null;
}

export interface PathExplanation {
  code: string;
  params: Record<string, unknown>;
}
export type PathBand = 'low' | 'medium' | 'high' | 'critical' | 'unknown';
export interface PathScore {
  score: number;
  band: PathBand;
  explanation: PathExplanation;
}
export interface PathCountryFit {
  country: string;
  annual_cost_eur: number | null;
  budget_ratio: number | null;
  programs_in_field: number;
  typical_ielts: number | null;
  reasons: PathExplanation[];
}
export interface PathDiagnosis {
  version: number;
  generated_at: string;
  recommended_path_type:
    | 'explore_first' | 'direct_masters' | 'funded_phd' | 'bachelor_abroad'
    | 'language_course_first' | 'scholarship_route' | 'next_intake';
  readiness: { score: number; band: 'strong' | 'possible' | 'risky' | 'not_recommended_now'; explanation: PathExplanation };
  language_gap: PathScore;
  document_gap: PathScore;
  timeline_pressure: PathScore;
  cost_pressure: PathScore;
  route_risk_level: 'low' | 'medium' | 'high';
  top_blockers: Array<{ code: string; severity: 'high' | 'medium' | 'low'; params: Record<string, unknown>; link: string | null }>;
  next_actions: Array<{ code: string; params: Record<string, unknown>; link: string | null }>;
  recommended_countries: PathCountryFit[];
  countries_to_avoid_for_now: PathCountryFit[];
  data_confidence: { level: 'high' | 'medium' | 'low'; missing_inputs: string[]; notes: PathExplanation[] };
  target_deadline: string | null;
  target_deadline_estimated: boolean;
}
